import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type {
  WellIntersectionAnalysisResponse,
  WellsResponse,
} from 'types/api-responses';
import { useWellIntersectionAnalysis } from 'components/well-page-content/hooks';
import { useWellUnitCandidates } from '../hooks';
import { toWorkspaceError } from '../utils';
import { wellsKeys } from '../query-keys';

export interface ScopedWell {
  /** The canonical well the production sections read, or undefined while loading. */
  well: WellsResponse | undefined;
  /**
   * When the well was last read successfully. Moves on every refetch, even one that
   * returns identical data, so a consumer that holds its own copy of the well (the Map's
   * one-shot query) can tell the well was re-read after an edit.
   */
  updatedAt: number;
  loading: boolean;
  /**
   * The canonical well could not be read. Distinct from `loading`: every card waits on
   * this well, so without it a failed fetch left them all on skeletons indefinitely.
   */
  error: boolean;
  /** Units intersecting this well — shared by Unit Details, Map and the Explorer. */
  intersection: WellIntersectionAnalysisResponse | undefined;
  intersectionLoading: boolean;
  selectedUnitIds: Set<string>;
  /** Matches UnitDetails' own signature: one row toggled at a time. */
  onUnitSelectionChange: (unitId: string, selected: boolean) => void;
}

/**
 * The canonical well behind the workspace's scoped staged well, plus the unit
 * intersection analysis.
 *
 * The detail serves staged wells, but the production sections read a `WellsResponse`
 * and its HATEOAS hrefs. So this fetches `/wells/:uwi` once for the scoped well and
 * shares it and the intersection across every card, rather than each card fetching.
 * @param {string | undefined} uwi The scoped well's UWI.
 * @param {number | undefined} workspaceId The workspace being read, so the intersection
 *   resolves as-of it rather than live.
 * @param {boolean} authored Whether the well was created in this workspace. An authored
 *   well has no canonical row, so its units come from the workspace's own endpoint.
 * @param {number | null} stagedWellId The authored well's staged row, its address there.
 * @return {ScopedWell} The well, the intersection, and the unit selection.
 */
export const useScopedWell = (
  uwi: string | undefined,
  workspaceId?: number,
  authored = false,
  stagedWellId: number | null = null
): ScopedWell => {
  /**
   * The reader's departures from the default selection, per unit: true for one they
   * turned on, false for one they turned off.
   *
   * An overlay rather than a replacement, because the standing assignment must stay
   * drawn whatever else is picked — see `selectedUnitIds` below. Mirrors the well page,
   * which is the behaviour production shows.
   */
  const [selectionOverrides, setSelectionOverrides] = useState<
    Map<string, boolean>
  >(new Map());

  const wellQuery = useQuery<WellsResponse>({
    // Scoped by workspace as well as uwi: the same well reads differently inside a draft
    // workspace than outside it, so the two must not share a cache entry.
    queryKey: [...wellsKeys.detail(uwi ?? ''), workspaceId ?? null],
    queryFn: async ({ signal }) => {
      try {
        return await api
          .get(`wells/${uwi}`, {
            // The workspace-scoped read. The backend resolves staged / canonical / promoted
            // behind one URL and one shape, which is what lets a well AUTHORED in the
            // workspace — no canonical row at all — resolve here rather than 404, and why no
            // card branches on `wellId === null`. It also fixes an EDITED existing well,
            // which the unscoped read answers with live canonical, missing the staged edits.
            searchParams: workspaceId ? { workspace_id: workspaceId } : {},
            signal,
          })
          .json<WellsResponse>();
      } catch (error) {
        throw await toWorkspaceError(error);
      }
    },
    enabled: !!uwi,
    retry: 1,
    // The canonical well does not change while the user reads a workspace, so this is
    // served from cache when stepping back to a well already visited.
    staleTime: 5 * 60 * 1000,
  });

  // Reused from the well page so Unit Details, Map and the Explorer agree on which
  // units intersect, but scoped to the workspace so a promoted one resolves as-of
  // promotion. The permit and stimulation queries carry the same directive.
  // Skipped for an authored well: the canonical analysis opens with `Well.find_by(uwi:)`
  // and there is no such row, so the request can only 404. The hook's own empty-uwi guard
  // is what holds it back.
  const { data: canonical, loading: canonicalLoading } =
    useWellIntersectionAnalysis(
      authored ? '' : (uwi ?? ''),
      undefined,
      workspaceId
    );

  /*
   * A well AUTHORED here has no canonical `wells` row, and Query::WellIntersectionAnalysis
   * opens with `Well.find_by(uwi:)` — so that read 404s for a synthetic uwi and can never
   * answer. Its units come instead from the workspace's own endpoint, computed live against
   * the draft wellbore geometry.
   *
   * Resolved HERE rather than in each card. The two envelopes are interchangeable by design
   * (the workspace view reuses the canonical development-area partial, so `geojson` and the
   * rest are identical; it only adds `bestFit`), and resolving once is what keeps Unit
   * Details, the Map and the Explorer looking at the same list — which they stopped doing
   * when only the table was moved across.
   */
  const candidates = useWellUnitCandidates(
    workspaceId ?? 0,
    // Needs BOTH addresses: the endpoint is nested under the workspace, so without one
    // the query would ask `/workspaces/0/...` and 404 rather than simply not run.
    authored && workspaceId ? stagedWellId : null
  );
  const intersection = authored ? candidates.data : canonical;
  const intersectionLoading = authored
    ? candidates.isLoading
    : canonicalLoading;

  /*
   * Which unit is highlighted before the reader picks one.
   *
   * The map draws only what is selected, so without a default nothing is ever shaded — and
   * the assigned row cannot supply it by being clicked, because the table refuses to toggle
   * an assigned row. Mirrors the well page's own seeding, with best fit added for an
   * authored well, which has no assignment until one is saved.
   */
  const defaultSelectedUnitIds = useMemo(() => {
    const units = intersection?.intersectingUnits ?? [];
    const primary =
      units.find((unit) => unit.assignedToUnit) ??
      units.find((unit) => unit.bestFit) ??
      units[0];

    return primary ? new Set([primary.name]) : new Set<string>();
  }, [intersection]);

  /** Whether this well actually holds an assignment, as opposed to a recommendation. */
  const hasAssignedUnit = useMemo(
    () => (intersection?.intersectingUnits ?? []).some((u) => u.assignedToUnit),
    [intersection]
  );

  // A new well scoped means a new list, so the previous well's picks must not carry over.
  const previousIntersection = useRef(intersection);
  useEffect(() => {
    if (intersection === previousIntersection.current) return;
    previousIntersection.current = intersection;
    // Bail when there is nothing to clear: a fresh Map every time is a new reference, so
    // React would re-render on every arrival of the list rather than only on a real reset.
    setSelectionOverrides((prev) => (prev.size === 0 ? prev : new Map()));
  }, [intersection]);

  // The default, as amended by the reader.
  const selectedUnitIds = useMemo(() => {
    const result = new Set(defaultSelectedUnitIds);
    selectionOverrides.forEach((selected, unitId) => {
      if (selected) result.add(unitId);
      else result.delete(unitId);
    });

    return result;
  }, [defaultSelectedUnitIds, selectionOverrides]);

  /*
   * One reader-chosen unit at a time — but the unit this well is ASSIGNED to stays drawn
   * alongside it, which is what production shows: the assignment is context for whatever
   * else is being compared against it, not another candidate to be swapped out. Where the
   * default is only a recommendation or the first row, it is a candidate like any other
   * and gives way to the pick.
   */
  const onUnitSelectionChange = useCallback(
    (unitId: string, selected: boolean) => {
      if (!selected) {
        setSelectionOverrides((prev) => new Map(prev).set(unitId, false));

        return;
      }

      setSelectionOverrides((prev) => {
        const next = new Map<string, boolean>();
        prev.forEach((wasSelected, id) => {
          if (wasSelected && id !== unitId) next.set(id, false);
        });
        if (!hasAssignedUnit) {
          defaultSelectedUnitIds.forEach((id) => {
            if (id !== unitId) next.set(id, false);
          });
        }
        next.set(unitId, true);

        return next;
      });
    },
    [defaultSelectedUnitIds, hasAssignedUnit]
  );

  return useMemo(
    () => ({
      well: wellQuery.data,
      updatedAt: wellQuery.dataUpdatedAt,
      loading: wellQuery.isPending,
      error: wellQuery.isError,
      intersection,
      intersectionLoading,
      selectedUnitIds,
      onUnitSelectionChange,
    }),
    [
      wellQuery.data,
      wellQuery.dataUpdatedAt,
      wellQuery.isPending,
      wellQuery.isError,
      intersection,
      intersectionLoading,
      selectedUnitIds,
      onUnitSelectionChange,
    ]
  );
};
